// Student — Homework page
import React, { useState, useEffect } from "react";
import { FiClock, FiCheckCircle, FiLink } from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";
import Modal from "../../components/shared/Modal";
import { toast } from "react-toastify";
import { format, isPast, differenceInDays } from "date-fns";

// Safe date converter (handles Firestore Timestamps)
const toDate = v => { if(!v) return new Date(); if(typeof v?.toDate==="function") return v.toDate(); return new Date(v); };





function isValidUrl(s) { try { new URL(s); return true; } catch { return false; } }

const SUBJECT_COLOR = { Mathematics:"#054bb4", Science:"#059669", English:"#7c3aed", "Social Studies":"#d97706", Nepali:"#dc2626", default:"#5d6169" };

export default function StudentHomework() {
  const { userData } = useAuth();
  const { getHomework, getSubmissions, addSubmission } = useData();

  const [hw, setHw]   = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState("all"); // all | pending | submitted | overdue
  const [selectedHw, setSelectedHw] = useState(null);
  const [form, setForm] = useState({ notes: "", linkUrl: "" });
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    const [h, s] = await Promise.all([
      getHomework({ classId: userData?.class }),
      getSubmissions({ studentId: userData?.uid }),
    ]);
    setHw(h); setSubs(s); setLoading(false);
  };

  const subMap = new Map(subs.map(s => [s.homeworkId, s]));

  const filtered = hw.filter(h => {
    const sub      = subMap.get(h.id);
    const overdue  = isPast(toDate(h.deadline));
    if (filter === "pending")   return !sub && !overdue;
    if (filter === "submitted") return !!sub;
    if (filter === "overdue")   return !sub && overdue;
    return true;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.notes.trim() && !form.linkUrl.trim()) { toast.error("Write a note or paste a link"); return; }
    if (form.linkUrl && !isValidUrl(form.linkUrl))  { toast.error("Enter a valid URL"); return; }
    setSaving(true);
    try {
      await addSubmission({ homeworkId: selectedHw.id, studentId: userData?.uid, studentName: userData?.name, ...form });
      toast.success("Submitted successfully! ✅");
      setSelectedHw(null); setForm({ notes: "", linkUrl: "" });
      load();
    } catch { toast.error("Failed to submit"); }
    finally { setSaving(false); }
  };

  const getStatus = (h) => {
    const sub = subMap.get(h.id);
    if (sub)                              return { label: sub.status === "graded" ? `Graded: ${sub.grade}` : "Submitted", color: "var(--green-600)", bg: "var(--green-50)" };
    if (isPast(toDate(h.deadline)))     return { label: "Overdue", color: "var(--red-600)", bg: "var(--red-50)" };
    const days = differenceInDays(toDate(h.deadline), new Date());
    if (days === 0) return { label: "Due Today!", color: "var(--red-600)", bg: "var(--red-50)" };
    if (days === 1) return { label: "Due Tomorrow", color: "var(--amber-600)", bg: "var(--amber-50)" };
    return { label: `${days} days left`, color: "var(--amber-600)", bg: "var(--amber-50)" };
  };

  const counts = {
    all: hw.length,
    pending: hw.filter(h => !subMap.has(h.id) && !isPast(toDate(h.deadline))).length,
    submitted: subs.length,
    overdue: hw.filter(h => !subMap.has(h.id) && isPast(toDate(h.deadline))).length,
  };

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="page-head">
        <div>
          <h1 className="page-title">📚 My Homework</h1>
          <p className="page-subtitle">Class {userData?.className} · Section {userData?.section}</p>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {[
          { key: "all",       label: "All",       count: counts.all },
          { key: "pending",   label: "Pending",   count: counts.pending,   color: "var(--amber-600)" },
          { key: "submitted", label: "Submitted", count: counts.submitted, color: "var(--green-600)" },
          { key: "overdue",   label: "Overdue",   count: counts.overdue,   color: "var(--red-600)" },
        ].map(t => (
          <button key={t.key} onClick={() => setFilter(t.key)} style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "7px 16px", borderRadius: "var(--r6)",
            border: `1.5px solid ${filter === t.key ? "var(--blue-700)" : "var(--border)"}`,
            background: filter === t.key ? "var(--blue-700)" : "#fff",
            color: filter === t.key ? "#fff" : "var(--gray-500)",
            fontWeight: 600, fontSize: ".84rem", cursor: "pointer",
            transition: "var(--t)", fontFamily: "Inter,sans-serif",
          }}>
            {t.label}
            <span style={{
              background: filter === t.key ? "rgba(255,255,255,.25)" : (t.color ? t.color + "18" : "var(--gray-100)"),
              color: filter === t.key ? "#fff" : (t.color || "var(--gray-500)"),
              fontSize: ".68rem", fontWeight: 800,
              padding: "1px 7px", borderRadius: 20,
            }}>{t.count}</span>
          </button>
        ))}
      </div>

      {loading ? <div className="loading-center"><div className="spinner" /></div> :
        filtered.length === 0 ? (
          <div className="card"><div className="empty-state">
            <div className="empty-icon">📚</div>
            <div className="empty-title">No homework found</div>
            <div className="empty-desc">
              {filter === "pending" ? "You're all caught up! Great work 🎉" : "No assignments in this category."}
            </div>
          </div></div>
        ) : (
          <div className="g g2">
            {filtered.map(h => {
              const sub = subMap.get(h.id);
              const st  = getStatus(h);
              const clr = SUBJECT_COLOR[h.subject] || SUBJECT_COLOR.default;
              return (
                <div key={h.id} className="card" style={{ overflow: "visible" }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,.08)"}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,.06)"}>
                  {/* colour top strip */}
                  <div style={{ height: 3, background: clr }} />
                  <div style={{ padding: "14px 16px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: ".7rem", fontWeight: 700, color: clr, display: "block", marginBottom: 4 }}>
                          {h.subject}
                        </span>
                        <h4 style={{ fontSize: ".95rem", fontWeight: 700, color: "var(--gray-900)", lineHeight: 1.3 }}>{h.title}</h4>
                      </div>
                      <span style={{ fontSize: ".72rem", fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: st.bg, color: st.color, whiteSpace: "nowrap", flexShrink: 0 }}>
                        {st.label}
                      </span>
                    </div>

                    {h.description && (
                      <p style={{ fontSize: ".82rem", color: "var(--gray-500)", marginBottom: 10, lineHeight: 1.55,
                        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {h.description}
                      </p>
                    )}

                    <div style={{ display: "flex", alignItems: "center", gap: 14, fontSize: ".75rem", color: "var(--gray-500)", marginBottom: 12 }}>
                      <span><FiClock size={12} style={{ display: "inline", marginRight: 3 }} />
                        {format(toDate(h.deadline), "MMM d, yyyy")}</span>
                      <span>By {h.teacherName}</span>
                    </div>

                    {/* Feedback */}
                    {sub?.feedback && (
                      <div style={{ background: "var(--green-50)", border: "1px solid var(--green-100)", borderRadius: "var(--r6)", padding: "8px 12px", marginBottom: 10 }}>
                        <div style={{ fontSize: ".72rem", fontWeight: 700, color: "var(--green-600)", marginBottom: 3 }}>Teacher Feedback</div>
                        <div style={{ fontSize: ".82rem", color: "var(--gray-700)" }}>{sub.feedback}</div>
                      </div>
                    )}

                    {/* Action */}
                    {sub ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--green-600)", fontSize: ".84rem", fontWeight: 600 }}>
                        <FiCheckCircle size={15} /> Submitted
                      </div>
                    ) : !isPast(toDate(h.deadline)) ? (
                      <button className="btn btn-primary btn-sm" style={{ width: "100%" }}
                        onClick={() => setSelectedHw(h)}>
                        Submit Assignment
                      </button>
                    ) : (
                      <div style={{ color: "var(--red-600)", fontSize: ".82rem", fontWeight: 600 }}>⚠ Deadline passed</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {/* Submit modal */}
      <Modal show={!!selectedHw} onClose={() => setSelectedHw(null)} title="Submit Assignment"
        footer={<>
          <button className="btn btn-ghost" onClick={() => setSelectedHw(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <><div className="spinner spinner-sm" /> Submitting…</> : "Submit"}
          </button>
        </>}>
        {selectedHw && (
          <>
            <div style={{ background: "var(--blue-50)", borderRadius: "var(--r6)", padding: "10px 14px", marginBottom: 14 }}>
              <div style={{ fontWeight: 700, fontSize: ".9rem" }}>{selectedHw.title}</div>
              <div style={{ fontSize: ".78rem", color: "var(--gray-500)", marginTop: 2 }}>
                Due: {format(toDate(selectedHw.deadline), "MMM d, yyyy · h:mm a")}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Your Answer / Notes</label>
              <textarea className="form-control" rows={4} placeholder="Write your answer or key points…"
                value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <FiLink size={12} /> Google Drive / External Link (optional)
              </label>
              <input className="form-control" placeholder="https://drive.google.com/..."
                value={form.linkUrl} onChange={e => setForm(p => ({ ...p, linkUrl: e.target.value }))} />
              <div className="form-hint">Share a Drive doc — set access to "Anyone with the link"</div>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
